import { metaLazy } from "@/lib/metaLazy";
import { currentConsentGeneration } from "@/lib/consentTransitions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import * as React from "react";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouter,
  useLocation,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { LocaleProvider, useLocale } from "@/contexts/LocaleContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ChunkErrorBanner } from "@/components/ChunkErrorBanner";
import { ScentTaxonomyLoader } from "@/components/ScentTaxonomyLoader";
import { PwaHead } from "@/components/PwaHead";
import { syncPwaInstallPath } from "@/lib/pwaInstall";

import { lazyIsland, isCrawlerUA } from "@/lib/lazyIsland";
import { quiet } from "@/lib/quiet-import";

// ⚡ عزل كود الأدمن: Cmd+K palette يُحمَّل فقط داخل /admin/*
const AdminCommandRoot = lazyIsland<{ children?: React.ReactNode }>(
  () => import("@/components/admin/AdminCommandRoot"),
  "AdminCommandRoot",
);

import appCss from "../styles.css?url";
import dmSansLatinWoff2 from "@fontsource-variable/dm-sans/files/dm-sans-latin-wght-normal.woff2?url";

import ogDefault from "../assets/og-default.jpg";
import { hasLocalSupabaseSession } from "@/lib/hasLocalSupabaseSession";
import { CONSENT_EVENT, hasAdsConsent, hasAnalyticsConsent } from "@/lib/cookie-consent";
import { noteOpenAiConsent, recordOpenAiPageView } from "@/lib/openaiPageViewLifecycle";
import { GA4_MEASUREMENT_ID } from "@/lib/ga4-config";
import { analyticsUrl } from "@/lib/analyticsUrl";
import { createPageViewNavigation } from "@/lib/pageViewNavigation";
import { captureAmbassadorReferral, type AmbassadorReferralLanding } from "@/lib/ambassadorReferral";

// Sonner (toast) لا يدخل حزمة الإقلاع — يُركَّب بعد أوّل رسم عند idle.
const Toaster = lazyIsland<{ position?: "top-center" }>(
  () => import("@/components/ui/sonner"),
  "Toaster",
);

// Lazy — لا نحمّل الوكيل الذكيّ ولا modal السفير من أوّل ثانية على الموبايل.
// كلاهما يفتح realtime/server-fn calls تثقل الإقلاع وتضخّم تأثير 504 من dev server.

const LecmoAgent = lazyIsland(() => import("@/components/LecmoAgent"), "LecmoAgent");
const AmbassadorWelcomeModal = lazyIsland(
  () => import("@/components/AmbassadorWelcomeModal"),
  "AmbassadorWelcomeModal",
);

const AnonymousWelcomeToast = lazyIsland(
  () => import("@/components/banners/BannerSlot"),
  "AnonymousToastSlot",
);

const ExitIntentCapture = lazyIsland(
  () => import("@/components/ExitIntentCapture"),
  "ExitIntentCapture",
);

const PushOptInPrompt = lazyIsland(
  () => import("@/components/PushOptInPrompt"),
  "PushOptInPrompt",
);
const WhatsAppFab = lazyIsland(() => import("@/components/WhatsAppFab"), "WhatsAppFab");

const isProductionBuild = import.meta.env.PROD;
const mobileAppMeta = isProductionBuild
  ? [
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "mobile-web-app-capable", content: "yes" },
    ]
  : [];
const progressiveAppLinks = isProductionBuild
  ? [
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ]
  : [];
// Meta Pixel: NO eager init — `fbevents.js` (98KB) + init are loaded lazily
// from `metaPixel.ensurePixelLoaded()` after FCP via requestIdleCallback in AppFrame.
// This removes ~800ms from FCP on mobile and fixes the duplicate-init warning.
// Google Analytics لا يُطلب قبل موافقة التحليلات. هذا يمنع 189KB ووقت تنفيذ
// Google من منافسة الشاشة الأولى، ويحافظ على Consent Mode عند التحميل اللاحق.

// شاشة طمأنة عند بطء التحميل: تظهر بعد ٤ ثوانٍ إن لم تكتمل تهيئة الواجهة
// (data-lecmo-ready="1"). تختفي تلقائيّاً عند الجاهزيّة. inline + dependency-free
// لتعمل قبل تحميل أيّ JS من React.
const slowLoadFallbackScript = {
  children: `(function(){try{var T=9000;var ID='lecmo-slow-load-fallback';var done=false;
function ready(){done=true;var el=document.getElementById(ID);if(el)el.remove();}
function show(){if(done||document.body.dataset.lecmoReady==='1'){ready();return;}
if(document.getElementById(ID))return;
var d=document.createElement('div');d.id=ID;
d.setAttribute('dir','rtl');
d.style.cssText='position:fixed;inset:0;z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:#0a0a0a;color:#e9e1d1;font-family:Cairo,system-ui,sans-serif;font-size:14px;text-align:center;padding:24px;';
d.innerHTML='<div style="width:36px;height:36px;border:2px solid #3a3a3a;border-top-color:#c9a84c;border-radius:50%;animation:lcmospin 1s linear infinite;"></div><div style="opacity:.85;">جارٍ تحميل التجربة…</div><div style="opacity:.5;font-size:12px;">يستغرق التحميل وقتاً أطول من المعتاد</div><style>@keyframes lcmospin{to{transform:rotate(360deg)}}</style>';
document.body.appendChild(d);}
var t=setTimeout(show,T);
window.addEventListener('load',function(){setTimeout(function(){clearTimeout(t);ready();},1200);});
var mo=new MutationObserver(function(){if(document.body&&document.body.dataset.lecmoReady==='1'){clearTimeout(t);ready();mo.disconnect();}});
if(document.body){mo.observe(document.body,{attributes:true,attributeFilter:['data-lecmoReady','data-lecmo-ready']});}
else{document.addEventListener('DOMContentLoaded',function(){mo.observe(document.body,{attributes:true,attributeFilter:['data-lecmo-ready']});});}
}catch(e){}})();`,
};


// لقطة dir/lang قبل hydration — يقرأ التفضيل المحفوظ ويضبط <html> فوراً
// لتفادي قفزة LTR→RTL عند أوّل رسم.
const localePreHydrationScript = {
  children: `(function(){try{var raw=localStorage.getItem('lux-locale-v3');var loc='en';
if(raw){var p=JSON.parse(raw);if(p&&(p.locale==='ar'||p.locale==='en'))loc=p.locale;}
else{var n=(navigator.language||'en').toLowerCase();if(n.indexOf('ar')===0)loc='ar';}
var h=document.documentElement;h.lang=loc;h.dir=loc==='ar'?'rtl':'ltr';
/* خطّ العربيّة لا يُحمَّل مسبقاً: preload كان يزاحم CSS الحاجب ويؤخّر أوّل رسم. */
}catch(e){}})();`,
};


// ⚡ LCP: شاشة الاستهلال تُرسَم داخل HTML الأوّل. هذا السكربت يقرّر قبل أيّ رسم
// هل تُعرض (زيارة أولى في الجلسة + مفعَّلة) أم تُخفى بـclass على <html>.
const introSplashPreHydrationScript = {
  children: `(function(){try{var F='lecmo:intro-splash:shown:v1';var C='lecmo:intro-splash:config:v1';
var hide=false;var mob=window.matchMedia('(max-width: 767px)').matches;
if(sessionStorage.getItem(F))hide=true;
if(!hide){var c={};var raw=localStorage.getItem(C);if(raw){c=JSON.parse(raw)||{};}
if(mob){if(c.mobile!==true)hide=true;}else if(c.desktop===false)hide=true;}
if(hide){document.documentElement.classList.add('lecmo-no-intro');}
else{sessionStorage.setItem(F,'1');}
}catch(e){document.documentElement.classList.add('lecmo-no-intro');}})();`,
};


const cookieConsentPreHydrationScript = {
  children: `(function(){var K='lecmo_cookie_consent_v1';function valid(r){try{var c=JSON.parse(r||'null');return !!(c&&typeof c.analytics==='boolean'&&typeof c.ads==='boolean'&&c.version==='1.0');}catch(e){return false;}}var saved=false;try{var m=document.cookie.match(/(?:^|;\\s*)lecmo_cookie_consent_v1=([^;]*)/);if(m)saved=valid(decodeURIComponent(m[1]));}catch(e){}if(!saved){try{saved=valid(localStorage.getItem(K));}catch(e){}}if(saved)document.documentElement.classList.add('lecmo-has-consent');})();`,
};





function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="t-display text-primary">404</h1>
        <h2 className="mt-4 t-h3 text-foreground">A scent not yet composed</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center border border-primary px-6 py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    // منع فهرسة الـ staging يُدار عبر X-Robots-Tag في src/server.ts (يستخدم request.url.hostname).
    const robotsMeta: Array<{ name: string; content: string }> = [];
    const googleSiteVerification = import.meta.env.VITE_GOOGLE_SITE_VERIFICATION as string | undefined;

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { name: "color-scheme", content: "light" },
        { name: "supported-color-schemes", content: "light" },
        /* Samsung Internet: disable Night Mode rewriting */
        { name: "samsung-internet-night-mode", content: "off" },
        ...robotsMeta,
        {
          name: "google-site-verification",
          content: "vT_v9GDHG3aAq6UP90cI2K5_891V1A_bwL9chNXwTN0",
        },
        { title: "Lecmo — Luxury Fragrances & Gift Sets" },
      {
        name: "description",
        content:
          "LECMO is a luxury fragrance story where refined taste meets emotion and fragrance artistry is born—through distinctive perfumes and elegant gift sets.",
      },
      { name: "author", content: "Lecmo" },
      { property: "og:title", content: "Lecmo — Luxury Fragrances & Gift Sets" },
      {
        property: "og:description",
        content: "LECMO is a luxury fragrance story where refined taste meets emotion and fragrance artistry is born—through distinctive perfumes and elegant gift sets.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/` },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "ar_AE" },
      { property: "og:image", content: ogDefault },
      { property: "og:image:secure_url", content: ogDefault },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "640" },
      { property: "og:image:alt", content: "Lecmo — Maison de Parfum" },
      { property: "og:site_name", content: "Lecmo" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogDefault },
      ...mobileAppMeta,
      { name: "twitter:title", content: "Lecmo — Luxury Fragrances & Gift Sets" },
      { name: "twitter:description", content: "LECMO is a luxury fragrance story where refined taste meets emotion and fragrance artistry is born—through distinctive perfumes and elegant gift sets." },
    ],
    links: [
      // Shopify CDN: كل صور المنتجات والمجموعات تُجلب من هنا — preconnect يوفّر ~200ms
      { rel: "preconnect", href: "https://cdn.shopify.com", crossOrigin: "" },
      // Cloud Storage: صور/فيديو الهيرو وخلفيّات الصفحات — preconnect يقصّر زمن LCP
      { rel: "preconnect", href: "https://oizqjftpdosdvisjblnb.supabase.co", crossOrigin: "" },
      // الخطوط مستضافة محلياً (WOFF2 عبر @fontsource) — لا CSS خارجيّ حاجب للرسم.
      // ⚡ preload لخطّ واحد فقط: أيّ preload إضافيّ يزاحم ملفّ CSS الحاجب على
      // شبكة الموبايل فيتأخّر أوّل رسم. باقي الخطوط تُطلَب بعد CSS بـfont-display: swap.
      { rel: "preload", as: "font", type: "font/woff2", href: dmSansLatinWoff2, crossOrigin: "anonymous" },

      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      ...progressiveAppLinks,
      ],
      scripts: [
        localePreHydrationScript,
        introSplashPreHydrationScript,
        cookieConsentPreHydrationScript,
        ...(isProductionBuild ? [slowLoadFallbackScript] : []),
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: RootErrorComponent,
});

function RootErrorComponent({ error: rawError, reset }: { error: unknown; reset: () => void }) {
  const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  const [copied, setCopied] = React.useState(false);
  // مهمّ: نطبع Error نفسه (لا message فقط) ليصل .stack لـ Server Logs
  React.useEffect(() => {
    console.error("[ROOT ERROR]", error);
    // Lazy import — لا نُدخل الأداة في bundle SSR
    void import("@/lib/errorDiagnostics").then(({ buildErrorDiagnostics, copyErrorDiagnostics, stashErrorDiagnostics }) => {
      const d = buildErrorDiagnostics(error);
      stashErrorDiagnostics(d);
      void copyErrorDiagnostics(d).then((ok) => { if (ok) setCopied(true); });
    });
  }, [error]);

  const handleCopy = async () => {
    const { buildErrorDiagnostics, copyErrorDiagnostics } = await import("@/lib/errorDiagnostics");
    const ok = await copyErrorDiagnostics(buildErrorDiagnostics(error));
    if (ok) setCopied(true);
  };

  return (
    <html lang="ar" dir="rtl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="robots" content="noindex" />
        <title>Lecmo — خطأ مؤقّت</title>
      </head>
      <body style={{ margin: 0, minHeight: "100svh", display: "grid", placeItems: "center", background: "#FBFAF7", color: "#1a1a1a", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, margin: "0 0 8px", fontWeight: 600 }}>حدث خلل مؤقّت</h1>
          <p style={{ margin: "0 0 20px", color: "#555" }}>عذرًا، تعذّر عرض هذه الصفحة. حاول مجدّدًا أو انسخ بيانات التشخيص.</p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={reset} style={{ padding: "10px 18px", background: "#1a1a1a", color: "#FBFAF7", border: "1px solid #1a1a1a", fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" }}>حاول مجدّدًا</button>
            <button onClick={handleCopy} style={{ padding: "10px 18px", background: "transparent", color: "#1a1a1a", border: "1px solid #1a1a1a", fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" }}>
              {copied ? "✓ نُسخ التشخيص" : "نسخ بيانات التشخيص"}
            </button>
            <a href="/" style={{ padding: "10px 18px", border: "1px solid #1a1a1a", color: "#1a1a1a", fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", textDecoration: "none" }}>الرئيسيّة</a>
          </div>
          {import.meta.env.DEV && (
            <pre style={{ marginTop: 18, textAlign: "start", whiteSpace: "pre-wrap", fontSize: 11, color: "#999" }}>{error?.stack ?? error?.message}</pre>
          )}
        </div>
      </body>
    </html>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  const pathname = useLocation({ select: (location) => location.pathname });
  React.useEffect(() => { syncPwaInstallPath(pathname); }, [pathname]);
  return (
    <html lang="en" dir="ltr" suppressHydrationWarning>
      <head>
        <HeadContent />
        <PwaHead pathname={pathname} production={isProductionBuild} />
      </head>
      <body suppressHydrationWarning>
        {/* No Meta noscript image: it would track without ads consent. */}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  React.useEffect(() => {
    // إشعار شاشة الطمأنة بأنّ React جاهز → تختفي تلقائيّاً
    if (typeof document !== "undefined" && document.body) {
      document.body.dataset.lecmoReady = "1";
    }
    quiet(import("@/lib/retry-resources").then((m) => m.installResourceRetry()));
    // التقاط أخطاء JavaScript الحيّة من العملاء → /admin/site-health
    quiet(import("@/lib/clientErrors").then((m) => m.installClientErrorCapture()));
    // التقاط معرّف نقرة Meta (fbclid) لبناء fbc وربط الطلب بالإعلان
    // Consent-gated: stored only with ads consent; memory-only before a choice.
    {
      // Landing URL + consent generation captured now, before the delayed load.
      const landingSearch = window.location.search;
      const landingGen = currentConsentGeneration();
      quiet(import("@/lib/metaClickId").then((m) => m.captureMetaClickId(landingSearch, landingGen)));
    }

    // جوجل (gclid + UTM): لا يُحفظ شيء قبل الموافقة، ويُنظَّف عند سحبها.
    // نحتفظ بسلسلة الاستعلام والمُحيل من أوّل تحميل حتّى لا تضيع القيم
    // إن وافق الزائر بعد تنقّله داخل الموقع.
    const initialSearch = window.location.search;
    const initialReferrer = document.referrer;
    const syncGoogleCapture = () => {
      quiet(import("@/lib/adsClickId").then((m) => m.captureAdsClickId(initialSearch)));
      quiet(import("@/lib/utmCapture").then((m) => m.captureUtmParams(initialSearch, initialReferrer)));
    };
    syncGoogleCapture();
    // سناب (ScCid): نفس سياسة الموافقة — لا حفظ قبلها، وتنظيف عند سحبها.
    const syncSnapCapture = () => {
      quiet(import("@/lib/snapClickId").then((m) => m.captureSnapClickId(initialSearch)));
    };
    syncSnapCapture();
    let offConsent = () => {};
    import("@/lib/cookie-consent").then((m) => {
      const syncMetaClick = () =>
        quiet(import("@/lib/metaClickId").then((mc) => mc.syncMetaClickConsent()));
      const syncSnapClick = () =>
        quiet(import("@/lib/snapClickId").then((sc) => sc.syncSnapClickConsent()));
      window.addEventListener(m.CONSENT_EVENT, syncGoogleCapture);
      window.addEventListener(m.CONSENT_EVENT, syncMetaClick);
      window.addEventListener(m.CONSENT_EVENT, syncSnapClick);
      offConsent = () => {
        window.removeEventListener(m.CONSENT_EVENT, syncGoogleCapture);
        window.removeEventListener(m.CONSENT_EVENT, syncMetaClick);
        window.removeEventListener(m.CONSENT_EVENT, syncSnapClick);
      };
    });
    return () => offConsent();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LocaleProvider>
            <AppFrame />
            <ChunkErrorBanner />
          </LocaleProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function AppFrame() {
  const router = useRouter();
  const navigate = router.navigate;
  const { pathname, searchStr } = useLocation();
  const { marketReady } = useLocale();
  const { user, loading: authLoading } = useAuth();

  const isAdminRoute = pathname.startsWith("/admin");
  const { hasAdminAccess, loading: roleLoading, rolesResolved } = useIsAdmin(isAdminRoute);
  const adminGateReady = !isAdminRoute || (!authLoading && !roleLoading);
  const showAdminGateLoader = isAdminRoute && !adminGateReady;
  const showAdminAccessDenied = isAdminRoute && !!user && rolesResolved && !hasAdminAccess;
  const hideAgent = isAdminRoute;
  const hideFloatingFabs = isAdminRoute || pathname === "/account/media";

  React.useEffect(() => {
    if (!isAdminRoute) return;
    if (authLoading || roleLoading) return;

    if (!user) {
      // حماية ضدّ سباق HMR / bootstrap كسول: إن وُجد رمز جلسة في
      // localStorage فالـ AuthContext سيُحدّث user خلال جزء من الثانية —
      // لا نطرد المستخدم لـ /auth بناءً على null عابر.
      if (hasLocalSupabaseSession()) return;

      void navigate({
        to: "/auth",
        search: { redirect: `${pathname}${searchStr}`, mode: "signin" },
        replace: true,
      });
      return;
    }

  }, [authLoading, roleLoading, user, hasAdminAccess, isAdminRoute, pathname, searchStr, navigate]);

  // الوكيل الذكيّ + النوافذ غير الحرجة: بعد تفاعل حقيقيّ أو idle متأخّر.
  // لا تنافس الشاشة الأولى ولا تدخل ضمن نافذة Lighthouse الحرجة.
  const [deferredReady, setDeferredReady] = React.useState(false);
  // Sonner: يُركَّب بعد أوّل رسم بقليل — يخرج من حزمة الإقلاع دون تأخير محسوس.
  const [toasterReady, setToasterReady] = React.useState(false);
  React.useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const show = () => setToasterReady(true);
    if (w.requestIdleCallback) w.requestIdleCallback(show, { timeout: 1500 });
    else window.setTimeout(show, 800);
  }, []);

  React.useEffect(() => {
    // الزواحف لا تتفاعل: لا نحمّل لها الجزر المؤجَّلة إطلاقاً.
    if (isCrawlerUA(navigator.userAgent)) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const trigger = () => setDeferredReady(true);
    const events = ["pointerdown", "keydown"] as const;
    events.forEach((event) => window.addEventListener(event, trigger, { once: true, passive: true }));
    const idle = w.requestIdleCallback
      ? w.requestIdleCallback(trigger, { timeout: 10_000 })
      : window.setTimeout(trigger, 10_000);
    return () => {
      events.forEach((event) => window.removeEventListener(event, trigger));
      if (w.cancelIdleCallback) w.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, []);

  const ambassadorLanding = React.useRef<AmbassadorReferralLanding | null>(null);
  React.useEffect(() => {
    ambassadorLanding.current = captureAmbassadorReferral();
  }, []);

  React.useEffect(() => {
    if (!marketReady) return;
    const landing = ambassadorLanding.current;
    if (!landing) return;
    quiet(
      import("@/lib/ambassadorTracking").then((m) =>
        m.processAmbassadorReferralIfPresent(landing),
      ),
    );
  }, [marketReady]);

  // وسم جوجل المشترك (GA4 + Ads): موافقتان مستقلّتان — موافقة الإعلانات وحدها
  // تُحمّل الوسم لـ Ads دون تشغيل GA4. التحميل بعد idle للزائر الموافق مسبقاً،
  // وفورياً عند الضغط على «أوافق» حتى لا تضيع أحداث الجلسة التالية للموافقة.
  React.useEffect(() => {
    if (!import.meta.env.PROD) return;
    const load = () => {
      quiet(import("@/lib/googleTagBootstrap").then((m) => m.ensureGoogleTagLoaded()));
      // تسخين هويّة الجلسة مبكّراً وفي الخلفيّة: السلّة والدفع يقرآن اللقطة
      // الجاهزة بلا انتظار الوسم.
      quiet(import("@/lib/gaSession").then((m) => m.primeGaSession()));
    };
    const idle = window.requestIdleCallback?.(load, { timeout: 5000 }) ?? window.setTimeout(load, 3500);
    window.addEventListener(CONSENT_EVENT, load);
    return () => {
      window.removeEventListener(CONSENT_EVENT, load);
      if (typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, []);


  // Meta Pixel: تحميل lazy بعد FCP (idle) — لا يحجب أوّل رسم.
  React.useEffect(() => {
    if (!marketReady) return;
    if (!import.meta.env.PROD) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const load = () => {
      // بكسلات الإعلانات لا تُحمَّل إلّا بموافقة صريحة على كوكيز الإعلانات
      if (!hasAdsConsent()) return;
      quiet(metaLazy(({ ensurePixelLoaded }) => ensurePixelLoaded())); // sends the initial PageView
      quiet(import("@/lib/tiktokPixel").then(({ ensureTikTokPixelLoaded }) => ensureTikTokPixelLoaded()));
      quiet(import("@/lib/snapPixel").then(({ ensureSnapPixelLoaded }) => ensureSnapPixelLoaded()));
      quiet(import("@/lib/googleAdsTag").then(({ ensureGoogleAdsConfigured }) => ensureGoogleAdsConfigured()));
      quiet(import("@/lib/openaiAdsPixel").then(({ ensureOpenAiAdsPixelLoaded }) => ensureOpenAiAdsPixelLoaded()));
    };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(load, { timeout: 4000 });
    } else {
      setTimeout(load, 2500);
    }
    // إن وافق الزائر لاحقاً من الشريط → نحمّل فوراً بلا إعادة تحميل الصفحة
    const onConsent = () => {
      // OpenAI: capture the transition synchronously (revocation drops pending).
      noteOpenAiConsent();
      // Meta: revoke/grant an already-installed fbq before anything else.
      quiet(import("@/lib/metaPixel").then((m) => m.syncMetaConsent()));
      load();
      // OpenAI: propagate grant/withdrawal to SDK + pending page views.
      quiet(import("@/lib/openaiAdsPixel").then((m) => m.syncOpenAiConsent()));
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, [marketReady]);

  // RUM: قياس Web Vitals لكلّ زائر حقيقيّ (lazy بعد idle لئلّا يثقل الإقلاع)
  React.useEffect(() => {
    if (!marketReady) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const load = () => {
      quiet(
        import("@/lib/perfTracker").then(({ initPerfTracker, setPerfContext }) => {
          setPerfContext({ userId: user?.id ?? null });
          void initPerfTracker();
        }),
      );
    };
    if (w.requestIdleCallback) {
      w.requestIdleCallback(load, { timeout: 5000 });
    } else {
      setTimeout(load, 3000);
    }
  }, [marketReady, user?.id]);

  React.useEffect(() => {
    if (!marketReady) return;
    quiet(import("@/lib/activityTracker").then((m) => m.initActivityTracker()));
    // سجّل المسار الحاليّ في مكدّس smartBack فور التحميل
    quiet(import("@/lib/smartBack").then((m) => m.trackSmartNav(window.location.pathname)));
    // OpenAI: initial view is registered here (navigation helper suppresses it);
    // the pixel flushes it once SDK + ads consent are ready.
    // Captured synchronously so a navigation before the import resolves
    // cannot be registered twice (registration is idempotent per page).
    // One shared module promise: .then callbacks run in attach order, so the
    // initial view always precedes later navigations.
    const openAiInitialPath = window.location.pathname;
    const openAi = import("@/lib/openaiAdsPixel");
    recordOpenAiPageView(openAiInitialPath); // sync: consent captured now
    quiet(openAi.then((m) => m.flushOpenAiPageView()));
    const onPageChange = createPageViewNavigation(
      window.location.pathname + window.location.search,
      (path) => {
        quiet(metaLazy(({ trackPageView }) => trackPageView()));
        // تأخير قصير حتّى يُحدّث الراوتر عنوان الصفحة (head) قبل إرسال page_view
        {
          const ga4Path = path;
          window.setTimeout(() => {
            quiet(
              import("@/lib/ga4").then(({ trackPageView: trackGa4PageView }) =>
                trackGa4PageView({ path: ga4Path, title: document.title }),
              ),
            );
          }, 60);
        }


        quiet(import("@/lib/googleAdsTag").then(({ trackAdsPageView }) => trackAdsPageView()));
        quiet(import("@/lib/tiktokPixel").then(({ trackPageView: trackTikTokPageView }) => trackTikTokPageView()));
        quiet(import("@/lib/snapPixel").then(({ trackPageView: trackSnapPageView }) => trackSnapPageView()));
        recordOpenAiPageView(path); // sync: consent captured at view time
        quiet(openAi.then((m) => m.flushOpenAiPageView()));
        quiet(
          import("@/lib/activityTracker").then((m) => m.trackPageView(path)),
        );
      },
    );
    const unsub = router.subscribe("onResolved", (evt) => {
      quiet(import("@/lib/smartBack").then((m) => m.trackSmartNav(evt.toLocation.pathname)));
      onPageChange(evt);
    });
    return unsub;
  }, [marketReady, router]);

  const appContent = (
    <>
      {showAdminGateLoader ? (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <p className="text-sm text-muted-foreground">جاري التحقق من الجلسة…</p>
        </div>
      ) : showAdminAccessDenied ? (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center">
          <main className="max-w-xl">
            <p className="font-sans text-xs uppercase tracking-[0.18em] text-muted-foreground">403</p>
            <h1 className="mt-3 font-serif text-3xl text-foreground">Admin access required</h1>
            <p className="mt-3 text-sm leading-7 text-muted-foreground">
              هذا الحساب مسجّل دخول، لكن لا يملك دور أدمن أو فريق. افتح الصفحة بحساب الأدمن أو أضف الصلاحية من لوحة الفريق.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex min-h-11 items-center justify-center border border-border px-5 text-xs uppercase tracking-[0.18em] text-foreground transition-colors hover:bg-muted"
            >
              Back
            </Link>
          </main>
        </div>
      ) : (
        <Outlet />
      )}
      {toasterReady ? (
        <React.Suspense fallback={null}>
          <Toaster position="top-center" />
        </React.Suspense>
      ) : null}

      {deferredReady ? (
        <ErrorBoundary fallback={() => null}>
          <React.Suspense fallback={null}>
            <AmbassadorWelcomeModal />
            <AnonymousWelcomeToast />
          </React.Suspense>
        </ErrorBoundary>
      ) : null}
      {!hideFloatingFabs && deferredReady ? (
        <ErrorBoundary fallback={() => null}>
          <React.Suspense fallback={null}>
            <WhatsAppFab />
          </React.Suspense>
        </ErrorBoundary>
      ) : null}
      {!hideAgent && deferredReady && (
        <ErrorBoundary fallback={() => null}>
          <React.Suspense fallback={null}>
            <LecmoAgent />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {!hideAgent && deferredReady && (
        <ErrorBoundary fallback={() => null}>
          <React.Suspense fallback={null}>
            <ExitIntentCapture />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {!hideAgent && deferredReady && (
        <ErrorBoundary fallback={() => null}>
          <React.Suspense fallback={null}>
            <PushOptInPrompt />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {pathname === "/shop" || pathname.startsWith("/product/") || pathname.startsWith("/collection/") ? (
        <ScentTaxonomyLoader />
      ) : null}
    </>
  );

  // ⚡ لا نحمّل مزوّد سياق الأدمن إلا داخل /admin/*
  return isAdminRoute ? (
    <React.Suspense fallback={appContent}>
      <AdminCommandRoot>{appContent}</AdminCommandRoot>
    </React.Suspense>
  ) : (
    appContent
  );
}


